//
import type { AxiosCollection } from '@/types/Components/Response'
import type { HistoryItem, IntegrationLogItem } from '@/types/Models/Assignment'
import type { Item, ItemData } from '@/types/Models/Item'
import type { ItemFilterType } from '@/types/Models/ItemFilterType'
import type { ItemsStoreState } from '@/types/Store'
import { defineStore } from 'pinia'
import { catchErrors } from '@/services/Helpers'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import ItemsProxy from '@/proxies/ItemsProxy'
import AssignmentProxy from '@/proxies/AssignmentProxy'
import ItemTransformer from '@/transformers/ItemTransformer'
import ItemProductProxy from '@/proxies/ItemProductProxy'
import ItemAddressesProxy from '@/proxies/ItemAddressesProxy'

const createDefaultState = (): ItemsStoreState => ({
  itemsList: [],
  itemsCount: 0,
  loading: false,
  checkedItems: [],
  errorsOnly: false,
  labelsErrorsOnly: false,
  fatalErrorsCount: 0,

  historyItems: [],
  historyLoading: false,
  historyCount: 0,
  integrationLogs: [],
  integrationLogsLoading: false,
  integrationLogsCount: 0,
  misingLabelItems: [],
  misingLabelItemsCount: 0,
  misingLabelItemsLoading: false,
})

const countFatalErrors = (items: Item[]): number =>
  items.filter((item: Item) =>
    Object.values(item.validationFlags || {}).some((f) => f?.level === 'fatal'),
  ).length

export const useItemStore = defineStore('item', {
  state: (): ItemsStoreState => createDefaultState(),
  getters: {
    items: (state: ItemsStoreState) => state.itemsList,
    isLoading: (state: ItemsStoreState) => state.loading,
    isErrorsOnly: (state: ItemsStoreState) => state.errorsOnly,
    getItemsCount: (state: ItemsStoreState) => state.itemsCount,
    isLabelsErrorsOnly: (state: ItemsStoreState) => state.labelsErrorsOnly,
    getMissingLabelItems: (state: ItemsStoreState) => state.misingLabelItems,
    getMissingLabelItemsCount: (state: ItemsStoreState) => state.misingLabelItemsCount,
    getMissingLabelItemsLoading: (state: ItemsStoreState) => state.misingLabelItemsLoading,
  },
  actions: {
    setErrorsOnly(flag: boolean) {
      this.errorsOnly = flag
    },
    setLabelsErrorsOnly(flag: boolean) {
      this.labelsErrorsOnly = flag
    },
    resetCheckedItems() {
      this.checkedItems = []
    },
    applyItemData({ itemId, item }: { itemId: string; item: ItemData }) {
      const index = this.itemsList.findIndex((i) => i.id === itemId)
      if (index === -1 || !this.itemsList[index]) {
        return
      }

      const transformedItem = ItemTransformer.fetch(item)
      Object.assign(this.itemsList[index]!, { ...transformedItem })
    },
    resetState() {
      Object.assign(this, createDefaultState())
    },
    updateItem({ item }: { item: Partial<ItemData> & { id: string } }) {
      return new ItemsProxy()
        .update(item.id, item)
        .catch(catchErrors)
        .finally(() => this.validateItem(item.id))
    },
    updateItemAddress(itemId: string, { id, itemAddressField }: { id: string; itemAddressField: Record<string, any> }) {
      return new ItemAddressesProxy()
        .update(id, itemAddressField)
        .catch(catchErrors)
        .finally(() => this.validateItem(itemId))
    },
    updateItemProduct(itemId: string, { id, itemProductField }: { id: string; itemProductField: Record<string, any> }) {
      return new ItemProductProxy()
        .update(id, itemProductField)
        .catch(catchErrors)
        .finally(() => this.validateItem(itemId))
    },
    recreateLabel(itemId: string) {
      return new ItemsProxy()
        .recreateLabel(itemId)
        .catch(catchErrors)
        .finally(() => this.validateItem(itemId))
    },
    validateItem(itemId: string) {
      return new ItemsProxy()
        .validate(itemId)
        .then((response) => {
          useAssignmentSummaryStore()
            .refresh()
            .then(() => {
              this.applyItemData({ itemId, item: response.data })
              this.fatalErrorsCount = countFatalErrors(this.itemsList)
            })
        })
        .catch(catchErrors)
    },
    updateCheckedItems(checked: string[]) {
      this.checkedItems = checked
    },
    deleteCheckedItems({ items }: { items: string[] }) {
      const promisesArray: Promise<any>[] = []
      items.forEach((id: string) => {
        promisesArray.push(new ItemsProxy().destroy(id))
      })
      return Promise.all(promisesArray)
    },
    fetchItems(params: ItemFilterType) {
      this.loading = true
      return new AssignmentProxy(params)
        .getItems(params.assignmentId)
        .then((response: AxiosCollection<ItemData>) => {
          this.itemsList = ItemTransformer.fetchCollection(response.data || [])
          this.itemsCount = response.count || 0
          this.fatalErrorsCount = countFatalErrors(this.itemsList)
        })
        .catch(catchErrors)
        .finally(() => {
          this.loading = false
        })
    },
    unmount() {
      this.resetState()
    },
    fetchHistory({ id, filter }: { id: string; filter: object }) {
      if (!id) {
        return
      }

      this.historyLoading = true
      return new ItemsProxy(filter)
        .getHistory(id)
        .then((response: AxiosCollection<HistoryItem>) => {
          this.historyItems = response.data.length ? response.data : []
          this.historyCount = response.count || 0
        })
        .catch(catchErrors)
        .finally(() => {
          this.historyLoading = false
        })
    },
    fetchIntegrationLogs({ id, filter }: { id: string; filter: object }) {
      if (!id) {
        return
      }

      this.integrationLogsLoading = true
      return new ItemsProxy(filter)
        .getIntegrationLogs(id)
        .then((response: AxiosCollection<IntegrationLogItem>) => {
          this.integrationLogs = response.data.length ? response.data : []
          this.integrationLogsCount = response.count || 0
        })
        .catch(catchErrors)
        .finally(() => {
          this.integrationLogsLoading = false
        })
    },
    fetchMissingLabelItems(assignmentId: string, filter: object) {
      this.misingLabelItemsLoading = true
      return new AssignmentProxy({ ...filter, onlyWithLabelsErrors: 1 })
        .getItems(assignmentId)
        .then((response: AxiosCollection<ItemData>) => {
          this.misingLabelItems = ItemTransformer.fetchCollection(response.data || [])
          this.misingLabelItemsCount = response.count || 0
        })
        .catch(catchErrors)
        .finally(() => {
          this.misingLabelItemsLoading = false
        })
    },
  },
})

export type ItemStore = ReturnType<typeof useItemStore>
